import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { find, propEq, equals, findIndex, isEmpty } from 'ramda';
import { isBlank } from 'utils/helperMethods';
import { createStartBidRequestEvent } from 'utils/gtmHelper';

import { Box, Typography } from '@material-ui/core';
import useMortgageSurvey from 'reducers/hooks/useMortgageSurvey';
import MortgageSurveyQuestionPresenter, {
  QUESTION_TYPES,
  compareQuestionValues,
} from 'presenters/MortgageSurveyQuestionPresenter';
import MortgageSurveyPresenter from 'presenters/MortgageSurveyPresenter';

import InputQuestion from './components/InputQuestion';
import SelectQuestion from './components/SelectQuestion';
import PriceTargetQuestion from './components/PriceTargetQuestion';
import DownPaymentTargetQuestion from './components/DownPaymentTargetQuestion';
import AnnualGrossIncomeQuestion from './components/AnnualGrossIncomeQuestion';
import LocationQuestion from './components/LocationQuestion';
import TextFieldQuestion from './components/TextFieldQuestion';
import MortgageBalanceQuestion from './components/MortgageBalanceQuestion';
import CashOutSelectQuestion from './components/CashOutSelectQuestion';
import CashOutAmountQuestion from './components/CashOutAmountQuestion';
import ProgressBar from './components/ProgressBar';

import useStyles from './useStyles';

const QUESTION_COMPONENTS = {
  [QUESTION_TYPES.input]: InputQuestion,
  [QUESTION_TYPES.select]: SelectQuestion,
  [QUESTION_TYPES.priceTarget]: PriceTargetQuestion,
  [QUESTION_TYPES.downPaymentTarget]: DownPaymentTargetQuestion,
  [QUESTION_TYPES.annualGrossIncome]: AnnualGrossIncomeQuestion,
  [QUESTION_TYPES.location]: LocationQuestion,
  [QUESTION_TYPES.textField]: TextFieldQuestion,
  [QUESTION_TYPES.mortgageBalance]: MortgageBalanceQuestion,
  [QUESTION_TYPES.cashOutAmount]: CashOutAmountQuestion,
  [QUESTION_TYPES.cashOutSelect]: CashOutSelectQuestion,
};

const MortgageSurvey = props => {
  const classes = useStyles();
  const { initialValues, initialPassing, questions, onSelect } = props;
  const [questionEntered, setQuestionEntered] = useState(false);
  const [startedSurvey, setStartedSurvey] = useState(false);

  const {
    incrementPastStepsCount,
    lastStepId,
    pastStepsCount,
    surveyValues,
    discardSteps,
    changeSurveyFieldValue,
    submitCurrentQuestion,
    setInitialSurveyValues,
    changeCurrentStepValidity,
    isValidCurrentStep,
    setSurveyPassed,
    setSurveyChanged,
    manuallySettedInitialSurveyValues,
  } = useMortgageSurvey();

  const toggleQuestionEntered = entered => setQuestionEntered(entered);

  useEffect(() => {
    toggleQuestionEntered(false);
    setTimeout(() => toggleQuestionEntered(true), 100);
    discardSteps();
    if (!isEmpty(initialValues)) {
      setInitialSurveyValues(initialValues);
    }
  }, []);

  const handleStartSurvey = () => {
    if (!startedSurvey) {
      createStartBidRequestEvent();
    }
    setStartedSurvey(true);
  };
  const calculateSurveyQuestion = () => {
    const currentQuestion = find(({ questionId, depends }) => {
      if (questionId <= lastStepId) return false;
      if (isBlank(depends)) return true;

      return depends.every(({ field, fieldValue, rule }) => {
        const currentValue = MortgageSurveyPresenter[field](surveyValues);
        return compareQuestionValues(currentValue, fieldValue, rule);
      });
    })(questions);

    const currentQuestionIndex = findIndex(
      propEq('questionId', MortgageSurveyQuestionPresenter.questionId(currentQuestion)),
    )(questions);

    const questionsLeftCount = questions.length - 1 - currentQuestionIndex;
    const totalQuestionsCount = pastStepsCount + questionsLeftCount + 1;
    const currentStep = pastStepsCount + 1;
    const isLastQuestion = equals(totalQuestionsCount, currentStep);

    return { currentQuestion, totalQuestionsCount, currentStep, isLastQuestion };
  };

  const { currentQuestion, totalQuestionsCount, currentStep, isLastQuestion } = calculateSurveyQuestion();

  const type = MortgageSurveyQuestionPresenter.type(currentQuestion);
  const QuestionComponent = QUESTION_COMPONENTS[type];
  const isSelectQuestion = MortgageSurveyQuestionPresenter.isSelectQuestionType(type);
  const shouldShowBottomBar = totalQuestionsCount !== 1;

  const handleAnswerChange = attrs => {
    attrs.forEach(({ field, value }) => changeSurveyFieldValue({ field, value }));
    if (isSelectQuestion) handleNext();
  };

  const handleNext = () => {
    const currentQuestionId = MortgageSurveyQuestionPresenter.questionId(currentQuestion);
    submitCurrentQuestion(currentQuestionId);
    incrementPastStepsCount();
    toggleQuestionEntered(false);
    handleStartSurvey();
    setTimeout(() => toggleQuestionEntered(true), 100);
    if (isLastQuestion) {
      setSurveyPassed(true);
      setSurveyChanged(true);
    }
  };

  const passCredit = surveyValues.creditScore > 550 || surveyValues.creditScore === null;

  return (
    <Box className={classes.container}>
      <Box className={shouldShowBottomBar ? classes.surveyWithBar : classes.surveyNoBar}>
        <Box
          className={questionEntered ? classes.survey : classes.surveyEntering}
          id={currentQuestion.surveyValueFields[0]}
        >
          {passCredit && (
            <QuestionComponent
              question={currentQuestion}
              onChange={handleAnswerChange}
              surveyValues={surveyValues}
              onValidate={changeCurrentStepValidity}
              initialPassing={initialPassing && !manuallySettedInitialSurveyValues}
              isValid={isValidCurrentStep}
              onNext={handleNext}
              onSelect={onSelect}
            />
          )}
          {!passCredit && (
            <Box className={classes.noProducts}>
              <Typography variant="h1">No Mortgage Products</Typography>
              <Box mt={2} />
              <Typography variant="body1">
                We currently do not offer any mortgage products for credit scores less than 580.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {shouldShowBottomBar && (
        <Box className={classes.bottomBar}>
          <Box className={classes.progress}>
            <ProgressBar totalCount={totalQuestionsCount} currentProress={currentStep} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

MortgageSurvey.propTypes = {
  initialValues: MortgageSurveyPresenter.shape(),
  initialPassing: PropTypes.bool,
  questions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

MortgageSurvey.defaultProps = {
  initialValues: {},
  initialPassing: true,
};

export default MortgageSurvey;



// WEBPACK FOOTER //
// ./src/sharedComponents/MortgageSurvey/MortgageSurvey.js